// components/products/SmartProductForm.jsx
import React, { useState, useCallback, useMemo, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  Settings2,
  Package,
  Weight,
  Gem,
  Award,
  Ruler,
  Palette,
  Store,
  IndianRupee,
  Image,
  Check,
  ChevronRight,
  ChevronDown,
  Info,
  Factory,
  ArrowLeft,
  Menu,
  X,
} from "lucide-react";
import MediaUpload from "../../../components/common/MediaUpload";
import Button from "../../../components/common/Button";
import {
  useEnhancedProductForm,
  useMediaHandler,
  PRODUCT_TYPE_ENUM,
  PRODUCT_SUBTYPE_ENUM,
  PRODUCT_STATUS_ENUM,
  PURITY_ENUM,
  METAL_COLOR_ENUM,
  PRODUCT_GENDER_ENUM,
  CRAFT_TYPE_ENUM,
  TECHNIQUE_ENUM,
} from "./enhancedProductFormHooks";

// Import all sections
import BasicInfoSection from "./sections/BasicInfoSection";
import ManufacturingSection from "./sections/ManufacturingSection";
import CertificationSection from "./sections/CertificationSection";
import CustomizationSection from "./sections/CustomizationSection";
import SizeVariantSection from "./sections/SizeVariantSection";
import StoneSection from "./sections/StoneSection";
import { HallmarkManagementSection } from "../ProductManagement/AdvancedComponents";
import {
  StockManagementSection,
  PricingConfigSection,
} from "./StockManagementComponents";
import CategoryHierarchyDropdown from "pages/Category/components/CategoryHierarchyDropdown";

// Static section configuration
const SECTION_CONFIG = {
  basic: {
    icon: Package,
    title: "Basic Information",
    required: true,
    description: "Name, type, and category",
  },
  weight: {
    icon: Weight,
    title: "Weight & Metal",
    required: true,
    description: "Metal type, purity, and weights",
  },
  stock: {
    icon: Store,
    title: "Stock & Location",
    required: true,
    description: "Inventory and branch allocation",
  },
  media: {
    icon: Image,
    title: "Images",
    required: false,
    description: "Product photos and videos",
  },
  stone: {
    icon: Gem,
    title: "Stones & Diamonds",
    required: false,
    description: "Stone details and pricing",
  },
  manufacturing: {
    icon: Factory,
    title: "Manufacturing",
    required: false,
    description: "Karigar and craft details",
  },
  hallmark: {
    icon: Award,
    title: "Hallmark & Certs",
    required: false,
    description: "BIS hallmark and certifications",
  },
  size: {
    icon: Ruler,
    title: "Sizes & Variants",
    required: false,
    description: "Available sizes and variants",
  },
  customization: {
    icon: Palette,
    title: "Customization",
    required: false,
    description: "Customer personalization options",
  },
  pricing: {
    icon: IndianRupee,
    title: "Pricing Rules",
    required: false,
    description: "Custom pricing configuration",
  },
};

// ============= MOBILE-FRIENDLY INPUT COMPONENT =============
const MobileInput = memo(
  ({ label, required, error, children, className = "" }) => (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
    </div>
  ),
);

MobileInput.displayName = "MobileInput";

// ============= MOBILE-FRIENDLY SELECT COMPONENT =============
const MobileSelect = memo(
  ({
    label,
    name,
    value,
    onChange,
    options,
    placeholder,
    required,
    error,
    className = "",
  }) => (
    <MobileInput
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <select
        name={name}
        value={value || ""}
        onChange={onChange}
        className={`
        w-full px-3 py-3 md:py-2.5 
        text-base md:text-sm
        border rounded-lg
        bg-white
        appearance-none
        min-h-[48px] md:min-h-[42px]
        focus:ring-2 focus:ring-primary/20 focus:border-primary
        ${error ? "border-red-300" : "border-gray-300"}
      `}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: "right 0.5rem center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "1.5em 1.5em",
          paddingRight: "2.5rem",
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option
            key={typeof opt === "string" ? opt : opt.value}
            value={typeof opt === "string" ? opt : opt.value}
          >
            {typeof opt === "string" ? opt : opt.label}
          </option>
        ))}
      </select>
    </MobileInput>
  ),
);

MobileSelect.displayName = "MobileSelect";

// ============= MOBILE-FRIENDLY TEXT INPUT COMPONENT =============
const MobileTextInput = memo(
  ({
    label,
    name,
    value,
    onChange,
    placeholder,
    type = "text",
    required,
    error,
    readOnly,
    step,
    min,
    className = "",
  }) => (
    <MobileInput
      label={label}
      required={required}
      error={error}
      className={className}
    >
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        step={step}
        min={min}
        autoComplete="off"
        className={`
        w-full px-3 py-3 md:py-2.5 
        text-base md:text-sm
        border rounded-lg
        min-h-[48px] md:min-h-[42px]
        focus:ring-2 focus:ring-primary/20 focus:border-primary
        ${error ? "border-red-300" : "border-gray-300"}
        ${readOnly ? "bg-gray-50 text-gray-600" : "bg-white"}
      `}
      />
    </MobileInput>
  ),
);

MobileTextInput.displayName = "MobileTextInput";

// ============= QUICK CREATE FORM COMPONENT =============
const QuickCreateForm = memo(
  ({
    formData,
    handleChange,
    switchToAdvanced,
    displayMedia,
    handleMediaChange,
    handleMediaUpload,
    handleMediaDelete,
    mediaFiles,
    handlePendingRemovalToggle,
    mode,
    PRODUCT_TYPE_ENUM,
    PRODUCT_SUBTYPE_ENUM,
    PRODUCT_STATUS_ENUM,
    PURITY_ENUM,
    METAL_COLOR_ENUM,
    categories,
    setFormData,
    errors,
  }) => {
    // Handle category selection from CategoryHierarchyDropdown
    const handleCategorySelect = useCallback(
      (categoryId) => {
        if (!categoryId) {
          handleChange({ target: { name: "categories", value: [] } });
          return;
        }
        handleChange({ target: { name: "categories", value: [categoryId] } });
      },
      [handleChange],
    );

    // Get the first selected category ID for single-select dropdown
    const selectedCategoryId = useMemo(() => {
      if (
        !formData.categories ||
        !Array.isArray(formData.categories) ||
        formData.categories.length === 0
      ) {
        return null;
      }
      return formData.categories[0];
    }, [formData.categories]);

    return (
      <div className="space-y-4 md:space-y-6 pb-24 md:pb-6">
        {/* Quick Setup Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center">
                <Zap className="w-5 h-5 text-yellow-500 mr-2" />
                Quick Create Mode
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Add essential details now, complete the rest later
              </p>
            </div>
            <button
              type="button"
              onClick={switchToAdvanced}
              className="w-full sm:w-auto px-4 py-2.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              Switch to Advanced
            </button>
          </div>
        </div>

        {/* Essential Fields Only */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6 space-y-5">
          <h4 className="font-medium text-gray-900 border-b pb-3 text-base">
            Essential Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {/* Product Name */}
            <MobileTextInput
              label="Product Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Diamond Solitaire Ring"
              required
              error={errors.name}
              className="md:col-span-2"
            />

            {/* Type */}
            <MobileSelect
              label="Product Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={PRODUCT_TYPE_ENUM}
              required
              error={errors.type}
            />

            {/* Subtype */}
            <MobileSelect
              label="Product Subtype"
              name="subType"
              value={formData.subType}
              onChange={handleChange}
              options={PRODUCT_SUBTYPE_ENUM.map((s) => ({
                value: s,
                label: s.replace(/_/g, " "),
              }))}
              placeholder="Select Subtype"
              required
              error={errors.subType}
            />

            {/* Category - Using CategoryHierarchyDropdown */}
            <div className="md:col-span-2">
              <CategoryHierarchyDropdown
                label="Category"
                categories={categories || []}
                value={selectedCategoryId}
                onChange={handleCategorySelect}
                placeholder="Select Category"
                showFullPathInList={true}
                allowClear={true}
                error={errors.categories}
              />
            </div>

            {/* Purity */}
            <MobileSelect
              label="Purity"
              name="purity"
              value={formData.purity}
              onChange={handleChange}
              options={PURITY_ENUM}
              placeholder="Select Purity"
              required
              error={errors.purity}
            />

            {/* Metal Color */}
            <MobileSelect
              label="Metal Color"
              name="metalColor"
              value={formData.metalColor}
              onChange={handleChange}
              options={METAL_COLOR_ENUM}
              required
            />

            {/* Gross Weight */}
            <MobileTextInput
              label="Gross Weight (g)"
              name="grossWeight"
              value={formData.grossWeight}
              onChange={handleChange}
              type="number"
              step="0.001"
              placeholder="0.000"
              required
              error={errors.grossWeight}
            />

            {/* Net Weight */}
            <MobileTextInput
              label="Net Weight (g)"
              name="netWeight"
              value={formData.netWeight}
              onChange={handleChange}
              type="number"
              step="0.001"
              placeholder="0.000"
              required
              error={errors.netWeight}
            />

            {/* Karigar Code */}
            <MobileTextInput
              label="Karigar Code"
              name="karigarCode"
              value={formData.karigarCode}
              onChange={handleChange}
              placeholder="KAR001"
              required
              error={errors.karigarCode}
            />

            {/* Initial Stock */}
            <MobileTextInput
              label="Initial Stock Quantity"
              name="stock.totalQuantity"
              value={formData.stock?.totalQuantity}
              onChange={handleChange}
              type="number"
              min="0"
              placeholder="0"
              required
            />
            {formData.branchInfo?.primaryBranch && (
              <p className="mt-1 text-xs text-gray-500">
                Will be allocated to: {formData.branchInfo.primaryBranch}
              </p>
            )}

            {/* Status */}
            <MobileSelect
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={PRODUCT_STATUS_ENUM}
              className="md:col-span-2"
            />
          </div>
        </div>

        {/* Quick Image Upload */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-6">
          <h4 className="font-medium text-gray-900 mb-4 text-base">
            Product Images (Optional)
          </h4>
          <MediaUpload
            media={displayMedia}
            onChange={handleMediaChange}
            onUpload={handleMediaUpload}
            onDelete={handleMediaDelete}
            pendingRemovals={mediaFiles.pendingRemovals}
            onPendingRemovalToggle={
              mode === "edit" ? handlePendingRemovalToggle : null
            }
            mode={mode}
            maxFiles={5}
            compact={true}
          />
        </div>
      </div>
    );
  },
);

QuickCreateForm.displayName = "QuickCreateForm";

// ============= ADVANCED FORM COMPONENT =============
const AdvancedForm = memo(
  ({
    sections,
    expandedSections,
    completedSections,
    toggleSection,
    switchToQuick,
    formData,
    handleChange,
    setFormData,
    errors,
    mode,
    // All the handlers
    addStone,
    updateStone,
    removeStone,
    addCertification,
    updateCertification,
    removeCertification,
    addSize,
    removeSize,
    addCustomizationOption,
    updateCustomizationOption,
    removeCustomizationOption,
    addLocationStock,
    updateLocationStock,
    removeLocationStock,
    calculateTotalStock,
    // Display data
    categories,
    availableBranches,
    permissions,
    locationStocks,
    displayMedia,
    mediaFiles,
    handleMediaChange,
    handleMediaUpload,
    handleMediaDelete,
    handlePendingRemovalToggle,
    // Constants
    STONE_TYPE_ENUM,
    STONE_SHAPE_ENUM,
    STONE_SETTING_ENUM,
    CERTIFICATION_TYPE_ENUM,
    CERTIFICATION_AGENCY_ENUM,
    CRAFT_TYPE_ENUM,
    TECHNIQUE_ENUM,
    PURITY_ENUM,
    METAL_COLOR_ENUM,
    PRODUCT_TYPE_ENUM,
    PRODUCT_SUBTYPE_ENUM,
    PRODUCT_GENDER_ENUM,
  }) => {
    return (
      <div className="space-y-4 pb-24 md:pb-6">
        {/* Advanced Mode Header */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center">
                <Settings2 className="w-5 h-5 text-gray-600 mr-2" />
                Advanced Mode
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Complete control over all product details
              </p>
            </div>
            <button
              type="button"
              onClick={switchToQuick}
              className="w-full sm:w-auto px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              Switch to Quick Mode
            </button>
          </div>

          {/* Progress Indicator - Horizontal Scroll on Mobile */}
          <div className="mt-4 -mx-4 px-4 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 min-w-max pb-2">
              {Object.entries(sections).map(([key, section]) => {
                if (section.show === false) return null;
                const isComplete = completedSections.has(key);
                const isActive = expandedSections.has(key);

                return (
                  <div
                    key={key}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap ${
                      isComplete
                        ? "bg-green-100 text-green-800"
                        : isActive
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {isComplete && <Check className="w-3 h-3" />}
                    {section.title}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="space-y-3">
          {Object.entries(sections).map(([key, section]) => {
            if (section.show === false) return null;
            const isExpanded = expandedSections.has(key);
            const isComplete = completedSections.has(key);
            const Icon = section.icon;

            return (
              <div
                key={key}
                className={`bg-white rounded-xl shadow-sm border transition-colors ${
                  isExpanded ? "border-blue-300" : "border-gray-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(key)}
                  className="w-full px-4 md:px-6 py-4 flex items-center justify-between hover:bg-gray-50 active:bg-gray-100 transition-colors rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2.5 md:p-2 rounded-lg ${
                        isComplete
                          ? "bg-green-100 text-green-600"
                          : isExpanded
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-left">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-medium text-gray-900 text-sm md:text-base">
                          {section.title}
                        </h4>
                        {section.required && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                            Required
                          </span>
                        )}
                        {isComplete && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <p className="text-xs md:text-sm text-gray-500 mt-0.5 hidden sm:block">
                        {section.description}
                      </p>
                    </div>
                  </div>
                  <div className="p-1">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-4 md:px-6 pb-4 md:pb-6">
                    <div className="border-t pt-4 md:pt-6">
                      {key === "basic" && (
                        <BasicInfoSection
                          formData={formData}
                          handleChange={handleChange}
                          errors={errors}
                          mode={mode}
                          PRODUCT_TYPE_ENUM={PRODUCT_TYPE_ENUM}
                          PRODUCT_SUBTYPE_ENUM={PRODUCT_SUBTYPE_ENUM}
                          PRODUCT_GENDER_ENUM={PRODUCT_GENDER_ENUM}
                          categories={categories}
                        />
                      )}

                      {key === "weight" && (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <MobileSelect
                              label="Purity"
                              name="purity"
                              value={formData.purity}
                              onChange={handleChange}
                              options={PURITY_ENUM}
                              placeholder="Select Purity"
                              required
                            />
                            <MobileSelect
                              label="Metal Color"
                              name="metalColor"
                              value={formData.metalColor}
                              onChange={handleChange}
                              options={METAL_COLOR_ENUM}
                              required
                            />
                            <MobileTextInput
                              label="Gross Weight (g)"
                              name="grossWeight"
                              value={formData.grossWeight}
                              onChange={handleChange}
                              type="number"
                              step="0.001"
                              required
                            />
                            <MobileTextInput
                              label="Net Weight (g)"
                              name="netWeight"
                              value={formData.netWeight}
                              onChange={handleChange}
                              type="number"
                              step="0.001"
                              required
                            />
                          </div>
                        </div>
                      )}

                      {key === "stone" && (
                        <StoneSection
                          formData={formData}
                          addStone={addStone}
                          updateStone={updateStone}
                          removeStone={removeStone}
                          STONE_TYPE_ENUM={STONE_TYPE_ENUM}
                          STONE_SHAPE_ENUM={STONE_SHAPE_ENUM}
                          STONE_SETTING_ENUM={STONE_SETTING_ENUM}
                          errors={errors}
                        />
                      )}

                      {key === "manufacturing" && (
                        <ManufacturingSection
                          formData={formData}
                          handleChange={handleChange}
                          errors={errors}
                          CRAFT_TYPE_ENUM={CRAFT_TYPE_ENUM}
                          TECHNIQUE_ENUM={TECHNIQUE_ENUM}
                        />
                      )}

                      {key === "hallmark" && (
                        <div className="space-y-6">
                          <HallmarkManagementSection
                            formData={formData}
                            onChange={handleChange}
                            errors={errors}
                          />
                          <div className="border-t pt-6">
                            <CertificationSection
                              formData={formData}
                              addCertification={addCertification}
                              updateCertification={updateCertification}
                              removeCertification={removeCertification}
                              CERTIFICATION_TYPE_ENUM={CERTIFICATION_TYPE_ENUM}
                              CERTIFICATION_AGENCY_ENUM={
                                CERTIFICATION_AGENCY_ENUM
                              }
                              errors={errors}
                            />
                          </div>
                        </div>
                      )}

                      {key === "stock" && (
                        <StockManagementSection
                          formData={formData}
                          locationStocks={locationStocks}
                          availableBranches={availableBranches}
                          permissions={permissions}
                          onAddLocation={addLocationStock}
                          onUpdateLocation={updateLocationStock}
                          onRemoveLocation={removeLocationStock}
                          calculateTotalStock={calculateTotalStock}
                          onChange={handleChange}
                          errors={errors}
                        />
                      )}

                      {key === "size" && (
                        <SizeVariantSection
                          formData={formData}
                          setFormData={setFormData}
                          addSize={addSize}
                          removeSize={removeSize}
                          errors={errors}
                        />
                      )}

                      {key === "customization" && (
                        <CustomizationSection
                          formData={formData}
                          setFormData={setFormData}
                          addCustomizationOption={addCustomizationOption}
                          updateCustomizationOption={updateCustomizationOption}
                          removeCustomizationOption={removeCustomizationOption}
                        />
                      )}

                      {key === "pricing" && (
                        <PricingConfigSection
                          formData={formData}
                          onChange={handleChange}
                          permissions={permissions}
                          productId={formData?._id || null}
                          locationStocks={locationStocks}
                          mode={mode}
                        />
                      )}

                      {key === "media" && (
                        <MediaUpload
                          media={displayMedia}
                          onChange={handleMediaChange}
                          onUpload={handleMediaUpload}
                          onDelete={handleMediaDelete}
                          pendingRemovals={mediaFiles.pendingRemovals}
                          onPendingRemovalToggle={
                            mode === "edit" ? handlePendingRemovalToggle : null
                          }
                          mode={mode}
                          maxFiles={10}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

AdvancedForm.displayName = "AdvancedForm";

// ============= MAIN SMART PRODUCT FORM COMPONENT =============
const SmartProductForm = ({ initialData = null, mode = "add" }) => {
  const navigate = useNavigate();
  const [formMode, setFormMode] = useState("quick");
  const [completedSections, setCompletedSections] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set(["basic"]));

  // Form hooks
  const {
    formData,
    setFormData,
    errors,
    submitting,
    handleChange: originalHandleChange,
    handleSubmit,
    categories,
    availableBranches,
    permissions,
    locationStocks,
    addLocationStock,
    updateLocationStock,
    removeLocationStock,
    calculateTotalStock,
    addStone,
    updateStone,
    removeStone,
    addCertification,
    updateCertification,
    removeCertification,
    addSize,
    removeSize,
    addCustomizationOption,
    updateCustomizationOption,
    removeCustomizationOption,
    STONE_TYPE_ENUM,
    STONE_SHAPE_ENUM,
    STONE_SETTING_ENUM,
    CERTIFICATION_TYPE_ENUM,
    CERTIFICATION_AGENCY_ENUM,
    CRAFT_TYPE_ENUM,
    TECHNIQUE_ENUM,
  } = useEnhancedProductForm(initialData, mode);

  const {
    mediaFiles,
    displayMedia,
    handleMediaUpload,
    handleMediaDelete,
    handlePendingRemovalToggle,
    handleMediaChange,
  } = useMediaHandler(initialData, mode);

  // Wrap handleChange to prevent unnecessary re-renders
  const handleChange = useCallback(
    (e) => {
      originalHandleChange(e);
    },
    [originalHandleChange],
  );

  // Memoize sections configuration
  const sections = useMemo(() => {
    const sectionsWithDynamicData = { ...SECTION_CONFIG };
    if (formData.hasStones) {
      sectionsWithDynamicData.stone.show = true;
    } else {
      sectionsWithDynamicData.stone.show = false;
    }
    return sectionsWithDynamicData;
  }, [formData.hasStones]);

  // Memoize callbacks
  const switchToAdvanced = useCallback(() => setFormMode("advanced"), []);
  const switchToQuick = useCallback(() => setFormMode("quick"), []);

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(section)) {
        newExpanded.delete(section);
      } else {
        newExpanded.add(section);
      }
      return newExpanded;
    });
  }, []);

  const onFormSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      await handleSubmit(mediaFiles, displayMedia);
    },
    [handleSubmit, mediaFiles, displayMedia],
  );

  return (
    <form onSubmit={onFormSubmit} className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="bg-white border-b sticky top-0 z-20 safe-area-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Header */}
          <div className="flex md:hidden items-center justify-between h-14">
            <button
              type="button"
              onClick={() => navigate("/products")}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-base font-semibold text-gray-900 truncate px-2">
              {mode === "add" ? "Create Product" : "Edit Product"}
            </h1>
            <div className="w-9" /> {/* Spacer for centering */}
          </div>

          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                {mode === "add"
                  ? "Create Product"
                  : `Edit: ${formData.name || "Product"}`}
              </h1>
              {formData.productId && (
                <span className="ml-3 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  #{formData.productId}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {formMode === "quick" ? "Quick Mode" : "Advanced Mode"}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => navigate("/products")}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                loading={submitting}
                disabled={submitting}
              >
                {submitting
                  ? "Saving..."
                  : mode === "add"
                    ? "Create"
                    : "Update"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
        {formMode === "quick" ? (
          <QuickCreateForm
            formData={formData}
            handleChange={handleChange}
            switchToAdvanced={switchToAdvanced}
            displayMedia={displayMedia}
            handleMediaChange={handleMediaChange}
            handleMediaUpload={handleMediaUpload}
            handleMediaDelete={handleMediaDelete}
            mediaFiles={mediaFiles}
            handlePendingRemovalToggle={handlePendingRemovalToggle}
            mode={mode}
            PRODUCT_TYPE_ENUM={PRODUCT_TYPE_ENUM}
            PRODUCT_SUBTYPE_ENUM={PRODUCT_SUBTYPE_ENUM}
            PRODUCT_STATUS_ENUM={PRODUCT_STATUS_ENUM}
            PURITY_ENUM={PURITY_ENUM}
            METAL_COLOR_ENUM={METAL_COLOR_ENUM}
            categories={categories}
            setFormData={setFormData}
            errors={errors}
          />
        ) : (
          <AdvancedForm
            sections={sections}
            expandedSections={expandedSections}
            completedSections={completedSections}
            toggleSection={toggleSection}
            switchToQuick={switchToQuick}
            formData={formData}
            handleChange={handleChange}
            setFormData={setFormData}
            errors={errors}
            mode={mode}
            addStone={addStone}
            updateStone={updateStone}
            removeStone={removeStone}
            addCertification={addCertification}
            updateCertification={updateCertification}
            removeCertification={removeCertification}
            addSize={addSize}
            removeSize={removeSize}
            addCustomizationOption={addCustomizationOption}
            updateCustomizationOption={updateCustomizationOption}
            removeCustomizationOption={removeCustomizationOption}
            addLocationStock={addLocationStock}
            updateLocationStock={updateLocationStock}
            removeLocationStock={removeLocationStock}
            calculateTotalStock={calculateTotalStock}
            categories={categories}
            availableBranches={availableBranches}
            permissions={permissions}
            locationStocks={locationStocks}
            displayMedia={displayMedia}
            mediaFiles={mediaFiles}
            handleMediaChange={handleMediaChange}
            handleMediaUpload={handleMediaUpload}
            handleMediaDelete={handleMediaDelete}
            handlePendingRemovalToggle={handlePendingRemovalToggle}
            STONE_TYPE_ENUM={STONE_TYPE_ENUM}
            STONE_SHAPE_ENUM={STONE_SHAPE_ENUM}
            STONE_SETTING_ENUM={STONE_SETTING_ENUM}
            CERTIFICATION_TYPE_ENUM={CERTIFICATION_TYPE_ENUM}
            CERTIFICATION_AGENCY_ENUM={CERTIFICATION_AGENCY_ENUM}
            CRAFT_TYPE_ENUM={CRAFT_TYPE_ENUM}
            TECHNIQUE_ENUM={TECHNIQUE_ENUM}
            PURITY_ENUM={PURITY_ENUM}
            METAL_COLOR_ENUM={METAL_COLOR_ENUM}
            PRODUCT_TYPE_ENUM={PRODUCT_TYPE_ENUM}
            PRODUCT_SUBTYPE_ENUM={PRODUCT_SUBTYPE_ENUM}
            PRODUCT_GENDER_ENUM={PRODUCT_GENDER_ENUM}
          />
        )}
      </div>

      {/* Mobile Floating Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 flex gap-3 md:hidden safe-area-bottom z-20">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/products")}
          disabled={submitting}
          className="flex-1 py-3 text-base"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          loading={submitting}
          disabled={submitting}
          className="flex-1 py-3 text-base"
        >
          {submitting ? "Saving..." : mode === "add" ? "Create" : "Update"}
        </Button>
      </div>

      {/* Styles for safe areas and scrollbar */}
      <style jsx>{`
        .safe-area-top {
          padding-top: env(safe-area-inset-top, 0px);
        }
        .safe-area-bottom {
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </form>
  );
};

export default SmartProductForm;
